/**
 * AnnotationMargin — Anmerkungen als Karten am Seitenrand.
 *
 * Alex 2026-09-30: „Die Darstellung von Anmerkungen und Markierungen an
 * der Seite schaut [woanders] ein bisschen besser aus." Jede Anmerkung ist
 * eine Karte in einer Randspalte, vertikal an ihrer markierten Stelle
 * ausgerichtet; überlappende Karten werden nach unten geschoben.
 *
 * Alex 2026-10-02/03 (Designvorschlag #5186, Schritte A und B):
 * - Die Farbe sagt „wer": Karte und Markierung tragen die Farbe des
 *   Autors (Registry für Agenten, Palette aus dem Namen für Menschen).
 * - Zwei Randspalten (`twoSided`): Karten werden nach Textstelle sortiert
 *   und so auf links und rechts verteilt, dass jede so nah wie möglich an
 *   ihrer Stelle steht und sich nichts stapelt.
 * - Eine gepunktete Linie in der Autorenfarbe verbindet die aktive Karte
 *   mit ihrer Passage.
 * - Karten außerhalb des Sichtfensters falten sich auf eine Zeile, damit
 *   der Rand bei vielen Anmerkungen ruhig bleibt.
 *
 * Ausrichtung: Anker = erstes Element mit `data-annotation-id` im
 * Lesemodus-Wurzelknoten. Karten ohne auffindbaren Anker (Passage wurde
 * geändert) stehen unten unter „Ohne Textstelle". Der Elternknoten der
 * Randspalten (`.read-with-margin`) ist der Bezugsrahmen für alle Maße.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { colorOf, initials, isAgentId } from '../utils/annotationAuthors'

export interface MarginReply {
  text: string
  author_id?: string | null
  author_name?: string | null
  created_at?: string | null
}

export interface MarginItem {
  id: number | string
  note_text: string
  author_id?: string | null
  author_name: string
  created_at?: string | null
  replies: MarginReply[]
}

interface Props {
  /** Wurzelknoten des gerenderten Lesemodus (dort liegen die Markierungen). */
  root: HTMLElement | null
  items: MarginItem[]
  /** Antwort speichern. Fehlt der Handler, gibt es kein Antwortfeld. */
  onReply?: (item: MarginItem, text: string) => Promise<boolean>
  /** Karten links und rechts verteilen statt nur rechts. */
  twoSided?: boolean
  /** Verteilung bei zwei Spalten: nach Platz (Vorgabe) oder nach Rolle,
   *  Menschen links und Agenten rechts. */
  sideMode?: 'space' | 'roles'
  /** Band, das die Schreibkarte (neue Anmerkung) gerade belegt: Karten
   *  weichen darunter aus, statt überdeckt zu werden. */
  reserved?: ReservedBand | null
}

export interface ReservedBand {
  /** Oberkante im Viewport. */
  viewportTop: number
  height: number
  side: Side
}

type Side = 'left' | 'right'

const GAP = 10
const COLLAPSED_HEIGHT = 30
const DEFAULT_HEIGHT = 84
/** So weit über und unter dem Sichtfenster bleiben Karten aufgeklappt. */
const VIEW_SLACK = 160

function cssEsc(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}

function relTime(iso?: string | null): string {
  if (!iso) return ''
  const t = Date.parse(iso.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z')
  if (Number.isNaN(t)) return ''
  const m = Math.max(0, Math.floor((Date.now() - t) / 60_000))
  if (m < 1) return 'gerade eben'
  if (m < 60) return `vor ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `vor ${h} h`
  const d = Math.floor(h / 24)
  return d < 30 ? `vor ${d} T` : new Date(t).toLocaleDateString('de-AT')
}

interface Placement {
  side: Side
  top: number
}

export function AnnotationMargin({ root, items, onReply, twoSided = false, reserved = null, sideMode = 'space' }: Props) {
  const leftRef = useRef<HTMLDivElement | null>(null)
  const rightRef = useRef<HTMLDivElement | null>(null)
  const cardRefs = useRef(new Map<string, HTMLDivElement>())
  const [placed, setPlaced] = useState<Record<string, Placement>>({})
  const [orphans, setOrphans] = useState<string[]>([])
  const [heights, setHeights] = useState<{ left: number; right: number }>({ left: 0, right: 0 })
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  const [hoverId, setHoverId] = useState<string | null>(null)
  const [replyFor, setReplyFor] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [saving, setSaving] = useState(false)
  const [connector, setConnector] = useState<{ d: string; color: string; x: number; y: number } | null>(null)

  const wrapperOf = useCallback((): HTMLElement | null => {
    const el = rightRef.current || leftRef.current
    return el ? el.parentElement : null
  }, [])

  const anchorOf = useCallback(
    (id: string): HTMLElement | null =>
      root ? (root.querySelector(`[data-annotation-id="${cssEsc(id)}"]`) as HTMLElement | null) : null,
    [root],
  )

  // Karten an ihren Textstellen ausrichten, auf die Seiten verteilen und
  // Überlappung nach unten schieben. Höhen stammen aus dem letzten Render;
  // der nächste Durchlauf (rAF, ResizeObserver, Intervall) zieht nach.
  const layout = useCallback(() => {
    const wrapper = wrapperOf()
    if (!wrapper || !root) return
    const base = wrapper.getBoundingClientRect().top
    const viewTop = -VIEW_SLACK
    const viewBottom = window.innerHeight + VIEW_SLACK
    const withAnchor: Array<{ id: string; want: number; visible: boolean }> = []
    const without: string[] = []
    for (const it of items) {
      const id = String(it.id)
      const el = anchorOf(id)
      if (el) {
        const r = el.getBoundingClientRect()
        withAnchor.push({ id, want: r.top - base, visible: r.bottom >= viewTop && r.top <= viewBottom })
      } else {
        without.push(id)
      }
    }
    withAnchor.sort((a, b) => a.want - b.want)

    const keepOpen = new Set<string>()
    for (const a of withAnchor) if (a.visible) keepOpen.add(a.id)
    if (activeId) keepOpen.add(activeId)
    if (hoverId) keepOpen.add(hoverId)
    if (replyFor) keepOpen.add(replyFor)
    const nextCollapsed = new Set<string>()
    for (const a of withAnchor) if (!keepOpen.has(a.id)) nextCollapsed.add(a.id)

    const heightOf = (id: string) =>
      nextCollapsed.has(id) ? COLLAPSED_HEIGHT : cardRefs.current.get(id)?.offsetHeight ?? DEFAULT_HEIGHT
    const cursor: Record<Side, number> = { left: 0, right: 0 }
    const next: Record<string, Placement> = {}
    const authorOfId = new Map(items.map((it) => [String(it.id), it.author_id]))
    // Belegtes Band der Schreibkarte, in Spaltenkoordinaten.
    const band = reserved
      ? { side: reserved.side, top: reserved.viewportTop - base, bottom: reserved.viewportTop - base + reserved.height }
      : null
    const avoidBand = (side: Side, y: number, h: number) =>
      band && band.side === side && y < band.bottom + GAP && y + h > band.top - GAP ? band.bottom + GAP : y
    for (const a of withAnchor) {
      let side: Side = 'right'
      const h = heightOf(a.id)
      if (twoSided && sideMode === 'roles') {
        // Fragen der Menschen links, Antworten der Agenten rechts.
        side = isAgentId(authorOfId.get(a.id)) ? 'right' : 'left'
      } else if (twoSided) {
        const yLeft = avoidBand('left', Math.max(a.want, cursor.left), h)
        const yRight = avoidBand('right', Math.max(a.want, cursor.right), h)
        // Die Seite, auf der die Karte näher an ihrer Stelle bleibt; bei
        // Gleichstand rechts, damit einzelne Anmerkungen wie bisher stehen.
        side = yLeft < yRight ? 'left' : 'right'
      }
      const y = avoidBand(side, Math.max(a.want, cursor[side]), h)
      next[a.id] = { side, top: Math.round(y) }
      cursor[side] = y + h + GAP
    }

    setPlaced((prev) => {
      const keys = Object.keys(next)
      if (
        keys.length === Object.keys(prev).length &&
        keys.every((k) => prev[k] && prev[k].top === next[k].top && prev[k].side === next[k].side)
      ) return prev
      return next
    })
    setOrphans((prev) => (prev.length === without.length && prev.every((v, i) => v === without[i]) ? prev : without))
    setHeights((prev) =>
      Math.abs(prev.left - cursor.left) < 1 && Math.abs(prev.right - cursor.right) < 1
        ? prev
        : { left: cursor.left, right: cursor.right },
    )
    setCollapsed((prev) => {
      if (prev.size === nextCollapsed.size && [...prev].every((id) => nextCollapsed.has(id))) return prev
      return nextCollapsed
    })

    // Verbindungslinie zwischen aktiver Karte und ihrer Passage.
    const focus = hoverId || activeId
    const card = focus ? cardRefs.current.get(focus) : null
    const mark = focus ? anchorOf(focus) : null
    if (focus && card && mark && next[focus]) {
      const wr = wrapper.getBoundingClientRect()
      const mr = mark.getBoundingClientRect()
      const cr = card.getBoundingClientRect()
      const side = next[focus].side
      const mx = (side === 'left' ? mr.left : mr.right) - wr.left
      const my = mr.top + mr.height / 2 - wr.top
      const cx = (side === 'left' ? cr.right : cr.left) - wr.left
      const cy = cr.top + 18 - wr.top
      const bend = Math.max(40, Math.abs(cx - mx) * 0.45)
      const d = `M ${mx} ${my} C ${mx + (side === 'left' ? -bend : bend)} ${my}, ${cx + (side === 'left' ? bend : -bend)} ${cy}, ${cx} ${cy}`
      const item = items.find((it) => String(it.id) === focus)
      const color = item ? colorOf(item.author_id, item.author_name) : '#999'
      setConnector((prev) => (prev && prev.d === d ? prev : { d, color, x: mx, y: my }))
    } else {
      setConnector((prev) => (prev ? null : prev))
    }
  }, [items, root, anchorOf, wrapperOf, twoSided, sideMode, activeId, hoverId, replyFor, reserved])

  useLayoutEffect(() => {
    const raf = window.requestAnimationFrame(layout)
    return () => window.cancelAnimationFrame(raf)
  }, [layout, replyFor, collapsed])

  useEffect(() => {
    if (!root) return
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => layout()) : null
    ro?.observe(root)
    const scroller = root.closest('.workspace') || window
    window.addEventListener('resize', layout)
    scroller.addEventListener('scroll', layout, { passive: true })
    // Bilder, Diagramme und Live-Edits verschieben Textstellen ohne
    // Resize-Ereignis am Wurzelknoten — günstig nachmessen.
    const intv = window.setInterval(layout, 700)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', layout)
      scroller.removeEventListener('scroll', layout)
      window.clearInterval(intv)
    }
  }, [root, layout])

  // Aktive Anmerkung: Markierung im Text hervorheben.
  useEffect(() => {
    const focus = hoverId || activeId
    if (!root || !focus) return
    const els = Array.from(root.querySelectorAll(`[data-annotation-id="${cssEsc(focus)}"]`))
    els.forEach((el) => el.classList.add('annotation-mark--active'))
    return () => els.forEach((el) => el.classList.remove('annotation-mark--active'))
  }, [root, activeId, hoverId, placed])

  // Klick auf eine Markierung im Text → zugehörige Karte in den Blick.
  useEffect(() => {
    if (!root) return
    const onClick = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.('[data-annotation-id]')
      if (!el || !root.contains(el)) return
      const id = el.getAttribute('data-annotation-id')
      if (!id) return
      setActiveId(id)
      cardRefs.current.get(id)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    const onOver = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.('[data-annotation-id]')
      setHoverId(el && root.contains(el) ? el.getAttribute('data-annotation-id') : null)
    }
    root.addEventListener('click', onClick)
    root.addEventListener('mouseover', onOver)
    root.addEventListener('mouseleave', () => setHoverId(null))
    return () => {
      root.removeEventListener('click', onClick)
      root.removeEventListener('mouseover', onOver)
    }
  }, [root])

  const submitReply = async (item: MarginItem) => {
    if (!onReply || !replyText.trim()) return
    setSaving(true)
    try {
      if (await onReply(item, replyText.trim())) {
        setReplyText('')
        setReplyFor(null)
      }
    } finally {
      setSaving(false)
    }
  }

  const renderCard = (it: MarginItem, positioned: boolean) => {
    const id = String(it.id)
    const color = colorOf(it.author_id, it.author_name)
    const agent = isAgentId(it.author_id)
    const isCollapsed = positioned && collapsed.has(id)
    const p = placed[id]
    const style: React.CSSProperties = positioned
      ? { position: 'absolute', top: p?.top ?? 0, left: 0, right: 0, visibility: p ? 'visible' : 'hidden' }
      : {}
    return (
      <div
        key={id}
        ref={(el) => {
          if (el) cardRefs.current.set(id, el)
          else cardRefs.current.delete(id)
        }}
        className={`annot-card${activeId === id ? ' annot-card--active' : ''}${hoverId === id ? ' annot-card--hover' : ''}${isCollapsed ? ' annot-card--collapsed' : ''}`}
        data-annotation-card={id}
        style={{ ...style, ['--annot-color' as string]: color }}
        onMouseEnter={() => setHoverId(id)}
        onMouseLeave={() => setHoverId((cur) => (cur === id ? null : cur))}
        onClick={(e) => {
          if ((e.target as Element).closest('button, textarea')) return
          setActiveId(id)
          anchorOf(id)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }}
      >
        <div className="annot-card__head">
          <span className={`annot-card__avatar${agent ? ' annot-card__avatar--agent' : ''}`} title={it.author_name}>
            {initials(it.author_name, it.author_id)}
          </span>
          <strong>{it.author_name}</strong>
          {isCollapsed ? (
            <span className="annot-card__peek">{it.note_text}</span>
          ) : (
            <span className="annot-card__time">{relTime(it.created_at)}</span>
          )}
        </div>
        {!isCollapsed && (
          <>
            <div className="annot-card__note">{it.note_text}</div>
            {it.replies.length > 0 && (
              <div className="annot-card__replies">
                {it.replies.map((r, i) => {
                  const rc = colorOf(r.author_id, r.author_name)
                  return (
                    <div key={i} className="annot-card__reply" style={{ ['--annot-color' as string]: rc }}>
                      <span className="annot-card__reply-head">
                        <span className={`annot-card__avatar annot-card__avatar--small${isAgentId(r.author_id) ? ' annot-card__avatar--agent' : ''}`}>
                          {initials(r.author_name, r.author_id)}
                        </span>
                        <strong>{r.author_name || 'Unbekannt'}</strong>
                        <span className="annot-card__time">{relTime(r.created_at)}</span>
                      </span>
                      <div>{r.text}</div>
                    </div>
                  )
                })}
              </div>
            )}
            {onReply &&
              (replyFor === id ? (
                <div className="annot-card__replyform">
                  <textarea
                    value={replyText}
                    autoFocus
                    placeholder="Antwort …"
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setReplyFor(null)
                        setReplyText('')
                      }
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submitReply(it)
                    }}
                  />
                  <div className="annot-card__actions">
                    <button type="button" onClick={() => { setReplyFor(null); setReplyText('') }}>
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      className="annot-card__send"
                      disabled={saving || !replyText.trim()}
                      onClick={() => void submitReply(it)}
                    >
                      Antworten
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="annot-card__replybtn" onClick={() => { setReplyFor(id); setReplyText('') }}>
                  Antworten
                </button>
              ))}
          </>
        )}
      </div>
    )
  }

  const anchored = items.filter((it) => !orphans.includes(String(it.id)))
  const orphaned = items.filter((it) => orphans.includes(String(it.id)))
  const onSide = (side: Side) => anchored.filter((it) => (placed[String(it.id)]?.side ?? 'right') === side)

  const track = (side: Side) => (
    <aside
      ref={side === 'left' ? leftRef : rightRef}
      className={`annot-margin annot-margin--${side}`}
      aria-label={side === 'left' ? 'Anmerkungen links' : 'Anmerkungen rechts'}
    >
      <div className="annot-margin__track" style={{ minHeight: heights[side] }}>
        {onSide(side).map((it) => renderCard(it, true))}
      </div>
      {side === 'right' && orphaned.length > 0 && (
        <div className="annot-margin__orphans">
          <div className="annot-margin__orphans-label">Ohne Textstelle</div>
          {orphaned.map((it) => renderCard(it, false))}
        </div>
      )}
    </aside>
  )

  return (
    <>
      {twoSided && track('left')}
      {track('right')}
      {connector && (
        <svg className="annot-connector" aria-hidden="true">
          <path d={connector.d} stroke={connector.color} strokeWidth={1.5} fill="none" strokeDasharray="3 3" />
          <circle cx={connector.x} cy={connector.y} r={3} fill={connector.color} />
        </svg>
      )}
    </>
  )
}
